import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ResiduosSelector from "@/components/puntolimpio/ResiduosSelector";
import SignaturePad from "@/components/admin/SignaturePad";
import { Search, Truck, Loader2, Camera } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errors";
import {
  adminListEventosPuntoLimpio, adminCrearSalida, adminSubirAdjuntoPuntoLimpio, getCatalogoResiduos,
} from "@/api";

const initialSalida = {
  fecha_hora: "", empresa: "", matricula2: "", peligrosidad: false,
  autorizacion: "", plantatratamiento: "", conductorempresa: "", incidencias2: false, tipoincidencia2: "",
};

export default function EventosHistorial() {
  const [eventos, setEventos] = useState([]);
  const [search, setSearch] = useState("");
  const [catalogo, setCatalogo] = useState([]);
  const [showSalidaForm, setShowSalidaForm] = useState(false);
  const [salida, setSalida] = useState(initialSalida);
  const [residuosSalida, setResiduosSalida] = useState([]);
  const [fotoAlbaran, setFotoAlbaran] = useState(null);
  const [firmaConformidad, setFirmaConformidad] = useState(null);
  const [firmaConductor, setFirmaConductor] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = (q) => adminListEventosPuntoLimpio(q ? { search: q } : {}).then(setEventos).catch(() => {});

  useEffect(() => {
    load();
    getCatalogoResiduos().then(setCatalogo).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    load(search);
  };

  const resetSalida = () => {
    setSalida(initialSalida);
    setResiduosSalida([]);
    setFotoAlbaran(null);
    setFirmaConformidad(null);
    setFirmaConductor(null);
  };

  const handleCrearSalida = async () => {
    setSaving(true);
    try {
      const res = await adminCrearSalida({
        ...salida,
        fecha_hora: new Date(salida.fecha_hora).toISOString(),
        residuos: residuosSalida.map((r) => ({ tipo: r.tipo, cantidad: r.cantidad, unidad: "kg" })),
      });
      const oid = res.arcgis_object_id;
      if (fotoAlbaran) await adminSubirAdjuntoPuntoLimpio(oid, "foto_albaran", fotoAlbaran);
      if (firmaConformidad) await adminSubirAdjuntoPuntoLimpio(oid, "firma_conformidad", new File([firmaConformidad], "firma_conformidad.png", { type: "image/png" }));
      if (firmaConductor) await adminSubirAdjuntoPuntoLimpio(oid, "firma_conductor", new File([firmaConductor], "firma_conductor.png", { type: "image/png" }));
      toast.success(`Salida registrada (ArcGIS #${oid})`);
      setShowSalidaForm(false);
      resetSalida();
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, "No se pudo registrar la salida"));
    } finally {
      setSaving(false);
    }
  };

  const canSubmitSalida = salida.fecha_hora && salida.empresa && salida.matricula2 && residuosSalida.length > 0;

  return (
    <div data-testid="eventos-historial-panel">
      <div className="flex flex-wrap gap-3 mb-4">
        <form onSubmit={handleSearch} className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Buscar por nombre, DNI o empresa..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="eventos-search-input" />
        </form>
        <Button variant="outline" className="border-municipal text-municipal" onClick={() => setShowSalidaForm(true)} data-testid="registrar-salida-btn">
          <Truck className="w-4 h-4 mr-2" /> Registrar salida
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Servicio</TableHead>
              <TableHead>Ciudadano / Empresa</TableHead>
              <TableHead className="hidden md:table-cell">Fecha</TableHead>
              <TableHead>Bonificación</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {eventos.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center text-gray-400 py-10">Sin eventos.</TableCell></TableRow>
            )}
            {eventos.map((e) => (
              <TableRow key={e.arcgis_object_id} data-testid={`evento-row-${e.arcgis_object_id}`}>
                <TableCell><Badge variant="outline">{e.Servicio?.replace(" Punto Limpio", "")}</Badge></TableCell>
                <TableCell>{e.nombre_apellidos || e.empresa}<br /><span className="text-xs text-gray-400">{e.dni_nie || e.matricula2}</span></TableCell>
                <TableCell className="hidden md:table-cell text-xs">{e.CreationDate ? new Date(e.CreationDate).toLocaleString() : "—"}</TableCell>
                <TableCell>{e.apuntado === "SI" ? <Badge className="bg-dorado/20 text-dorado border-dorado/30">Apuntado</Badge> : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showSalidaForm} onOpenChange={(v) => !saving && setShowSalidaForm(v)}>
        <DialogContent className="max-w-lg" data-testid="registrar-salida-dialog">
          <DialogHeader><DialogTitle className="text-municipal font-heading">Registrar salida / retirada</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block">Fecha y hora</Label>
                <Input type="datetime-local" value={salida.fecha_hora} onChange={(e) => setSalida({ ...salida, fecha_hora: e.target.value })} data-testid="salida-fecha-input" />
              </div>
              <div>
                <Label className="mb-1.5 block">Matrícula del camión</Label>
                <Input value={salida.matricula2} onChange={(e) => setSalida({ ...salida, matricula2: e.target.value })} data-testid="salida-matricula-input" />
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block">Empresa transportista</Label>
              <Input value={salida.empresa} onChange={(e) => setSalida({ ...salida, empresa: e.target.value })} data-testid="salida-empresa-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block">Nº DCS / Autorización</Label>
                <Input value={salida.autorizacion} onChange={(e) => setSalida({ ...salida, autorizacion: e.target.value })} data-testid="salida-autorizacion-input" />
              </div>
              <div>
                <Label className="mb-1.5 block">Planta de tratamiento</Label>
                <Input value={salida.plantatratamiento} onChange={(e) => setSalida({ ...salida, plantatratamiento: e.target.value })} data-testid="salida-planta-input" />
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block">Residuos retirados</Label>
              {catalogo.length > 0 && <ResiduosSelector catalogo={catalogo} value={residuosSalida} onChange={setResiduosSalida} />}
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={salida.peligrosidad} onCheckedChange={(v) => setSalida({ ...salida, peligrosidad: v })} id="peligrosidad" data-testid="salida-peligrosidad-checkbox" />
              <Label htmlFor="peligrosidad" className="font-normal cursor-pointer">Transporte de residuos peligrosos</Label>
            </div>
            <div>
              <Label className="mb-1.5 flex items-center gap-1.5"><Camera className="w-3.5 h-3.5" /> Foto del albarán (opcional)</Label>
              <Input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setFotoAlbaran(e.target.files?.[0] || null)} data-testid="salida-foto-albaran-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Firma de conformidad</Label>
              <SignaturePad onChange={setFirmaConformidad} testId="salida-firma-conformidad" />
            </div>
            <div>
              <Label className="mb-1.5 block">Firma del conductor</Label>
              <SignaturePad onChange={setFirmaConductor} testId="salida-firma-conductor" />
            </div>
          </div>
          <Button
            className="w-full bg-municipal hover:bg-municipal-dark text-white mt-2"
            onClick={handleCrearSalida}
            disabled={saving || !canSubmitSalida}
            data-testid="salida-submit-btn"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Registrar en ArcGIS
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
